/**
 * Network Service
 * Handles network connectivity detection and offline mode
 */

import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';

class NetworkService {
  listeners = [];
  currentStatus = {
    connected: true,
    connectionType: 'unknown'
  };
  statusCallbacks = [];
  
  /**
   * Initialize network monitoring
   */
  async initialize() {
    if (!Capacitor.isPluginAvailable('Network')) {
      console.log('Network plugin not available');
      return;
    }
    
    // Get initial status
    this.currentStatus = await Network.getStatus();
    console.log('Initial network status:', this.currentStatus);
    
    // Listen for changes
    const listener = Network.addListener('networkStatusChange', (status) => {
      console.log('Network status changed:', status);
      this.currentStatus = status;
      
      // Notify all registered callbacks
      this.statusCallbacks.forEach(callback => {
        try {
          callback(status);
        } catch (error) {
          console.error('Network callback error:', error);
        }
      });
    });
    
    this.listeners.push(listener);
  }
  
  /**
   * Get current network status
   */
  async getStatus() {
    try {
      this.currentStatus = await Network.getStatus();
      return this.currentStatus;
    } catch (error) {
      console.error('Failed to get network status:', error);
      return { connected: true, connectionType: 'unknown' };
    }
  }
  
  /**
   * Check if currently connected
   */
  isConnected() {
    return this.currentStatus.connected;
  }
  
  /**
   * Get connection type (wifi, cellular, none, unknown)
   */
  getConnectionType() {
    return this.currentStatus.connectionType;
  }
  
  /**
   * Register a callback for network status changes
   * @param {Function} callback - Called with status object
   * @returns {Function} Unsubscribe function
   */
  onStatusChange(callback) {
    this.statusCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.statusCallbacks = this.statusCallbacks.filter(cb => cb !== callback);
    };
  }
  
  /**
   * Wait for network connectivity
   * @param {number} timeout - Max wait time in ms
   * @returns {Promise<boolean>} True if connected within timeout
   */
  async waitForConnection(timeout = 30000) {
    if (this.currentStatus.connected) {
      return true;
    }
    
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        unsubscribe();
        resolve(false);
      }, timeout);
      
      const unsubscribe = this.onStatusChange((status) => {
        if (status.connected) {
          clearTimeout(timeoutId);
          unsubscribe();
          resolve(true);
        }
      });
    });
  }
  
  /**
   * Execute a function with retry on network failure
   * @param {Function} fn - Async function to execute
   * @param {number} maxRetries - Maximum retry attempts
   * @param {number} delayMs - Delay between retries
   */
  async executeWithRetry(fn, maxRetries = 3, delayMs = 1000) {
    let lastError;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Wait for connection if offline
        if (!this.isConnected()) {
          const connected = await this.waitForConnection(5000);
          if (!connected) {
            throw new Error('No network connection');
          }
        }
        
        return await fn();
      } catch (error) {
        lastError = error;
        console.warn(`Attempt ${attempt + 1}/${maxRetries} failed:`, error.message);
        
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, delayMs * (attempt + 1)));
        }
      }
    }
    
    throw lastError;
  }
  
  /**
   * Clean up listeners
   */
  cleanup() {
    this.listeners.forEach(listener => listener.remove());
    this.listeners = [];
    this.statusCallbacks = [];
  }
}

export const networkService = new NetworkService();
export default networkService;
